/*!
 * BOPF Zara Wishlist Heart   [v13 - hearts follow the shared saved list]
 *
 * v13 (2026-09-23): same code as v12, relabelled so a complete paste can be confirmed.
 *   The LAST line of this file must read: // END OF FILE - bopf-wishlist-heart v13
 *
 * v12 (2026-09-23): the website hearts now follow the ONE saved list that the
 * Saved page and the BOPF app both read (needs server v3.98 or later).
 *   - Un-hearting a piece when we know the shopper's email now removes it on the
 *     server too (POST /zara/wishlist/remove), so it also leaves the Saved page
 *     and the app. v11 removed it from this browser only, and it came back later.
 *     A remove that fails (server asleep, no signal) is retried on a later page
 *     within the hour (later than that, a change made in the app must win).
 *   - When an email is known, the hearts and the nav count load from the server
 *     (GET /zara/wishlist/handles, at most once every 5 minutes per tab, and only
 *     one read at a time across tabs), so a piece saved in the app shows as
 *     hearted here. A heart that was on the server but is gone now (removed in the
 *     app or another browser) is cleared here ONLY when the server read was
 *     complete (degraded:false). A partial read never clears anything. A read that
 *     fails or is cut off by leaving the page does not count, so the next page
 *     tries again. A server without /handles (older than v3.98) means no read.
 *   - Local-only saves still upload silently, as in v11. If the shopper removed
 *     that piece somewhere else, the server answers suppressed:true and the heart
 *     is cleared here instead of the piece being added back. A heart she tapped
 *     with her email known whose own POST was lost is re-sent as that tap
 *     (within the hour), so it is never suppressed by an older removal.
 *   - Product-page saves always carry a title: og:title, then the page title,
 *     then the product heading, then the handle. The site has no <title> or
 *     og:title tags today, and the server refused an empty title.
 *
 * v11 (2026-09-23): a heart tapped before the shopper gave an email (or after she
 * closed the email popup) was saved ONLY in this browser and never sent - it looked
 * saved but never reached her Saved page. Now every local-only save is sent (silently,
 * no confirmation email) the moment an email is known: when she enters one, on any
 * later page load, or straight away when she is logged in to her account.
 *
 * Replaces SWYM wishlist hearts on product and collection pages with
 * Zara-owned hearts. Click opens an editorial email-capture popup
 * (Hadiza voice), posts to /zara/wishlist/add, and routes to Sheets,
 * Omnisend, and a Resend confirmation email.
 *
 * SWYM continues to run in parallel during the transition period; we
 * just hide its UI. When SWYM is removed, only our hearts remain.
 *
 * Browser storage used (all per browser):
 *   localStorage   bopf_zara_email             the shopper's email
 *   localStorage   bopf_zara_saved             {handle: savedAtMs} - the hearts
 *   localStorage   bopf_zara_email_declined_at popup cooldown
 *   localStorage   bopf_zara_synced            {handle: ms} - hearts the server holds (v11)
 *   localStorage   bopf_zara_synced_for        which email bopf_zara_synced belongs to (v12)
 *   localStorage   bopf_zara_unsave_pending    {handle: {t, e}} removes still to confirm, 1 h (v12)
 *   localStorage   bopf_zara_add_pending       {handle: {t, e, c}} taps still to confirm, 1 h (v12)
 *   localStorage   bopf_zara_unsaved_at        {handle: ms} un-heart times, 10 min (v12)
 *   localStorage   bopf_zara_hydrate_lock      {e, t} a server read in flight, 25 s (v12)
 *   sessionStorage bopf_zara_hydrated          {e, t} last good server read in this tab (v12)
 *
 * Install:
 *   1. Save this file in the theme as: assets/bopf-wishlist-heart.js
 *   2. Add to layout/theme.liquid, just before </body>:
 *        <script src="{{ 'bopf-wishlist-heart.js' | asset_url }}?v=9" defer><\/script>
 *   3. Remove any older bopf-wishlist.js include from theme.liquid.
 *      (The older file intercepts SWYM's _swat.addToWishList, which we
 *       no longer want now that we're replacing SWYM's UI entirely.)
 */
(function() {
  "use strict";

  var ZARA_BACKEND = "https://bopf-concierge.onrender.com",
      STORAGE_EMAIL = "bopf_zara_email",
      STORAGE_SAVED = "bopf_zara_saved",
      STORAGE_DECLINED = "bopf_zara_email_declined_at",
      DECLINED_COOLDOWN_MS = 6048e5;

  // v12
  var VERSION = "v13",
      STORAGE_SYNCED = "bopf_zara_synced",
      STORAGE_SYNCED_FOR = "bopf_zara_synced_for",
      STORAGE_UNSAVE_PENDING = "bopf_zara_unsave_pending",
      STORAGE_ADD_PENDING = "bopf_zara_add_pending",
      STORAGE_UNSAVED_AT = "bopf_zara_unsaved_at",
      STORAGE_HYDRATE_LOCK = "bopf_zara_hydrate_lock",
      SESSION_HYDRATED = "bopf_zara_hydrated",
      HYDRATE_EVERY_MS = 3e5,            // at most one server read per 5 minutes per tab
      HYDRATE_LOCK_MS = 25e3,            // a read in flight (in any tab) holds off other reads this long
      UNSAVE_PENDING_TTL_MS = 36e5,      // retry a failed remove for up to 1 hour
      ADD_PENDING_TTL_MS = 36e5,         // re-send a lost heart tap as a tap for up to 1 hour
      UNSAVED_AT_TTL_MS = 6e5,           // keep un-heart times 10 minutes (stale-read guard)
      FLUSH_WAIT_MAX_MS = 8e3,           // the flush waits this long at most for the server read
      FLUSH_MAX = 40,
      EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  function log() {
    try {
      console.log.apply(console, [ "[bopf-heart]" ].concat([].slice.call(arguments)));
    } catch (e) {}
  }

  function lc(s) {
    return String(s == null ? "" : s).trim().toLowerCase();
  }

  function readJSON(key) {
    try {
      var raw = localStorage.getItem(key), v = raw ? JSON.parse(raw) : {};
      return v && typeof v === "object" && !Array.isArray(v) ? v : {};
    } catch (e) {
      return {};
    }
  }

  function writeJSON(key, obj) {
    try {
      localStorage.setItem(key, JSON.stringify(obj || {}));
    } catch (e) {}
  }

  // The key in map that stands for handle (exact match first, then case-insensitive).
  function findKey(map, handle) {
    if (!map || !handle) return null;
    if (Object.prototype.hasOwnProperty.call(map, handle)) return handle;
    var want = lc(handle), keys = Object.keys(map);
    for (var i = 0; i < keys.length; i++) if (lc(keys[i]) === want) return keys[i];
    return null;
  }

  function deleteHandle(map, handle) {
    var hit = false, want = lc(handle);
    Object.keys(map || {}).forEach(function(k) {
      if (k === handle || lc(k) === want) {
        delete map[k];
        hit = true;
      }
    });
    return hit;
  }

  function getEmail() {
    var em = "";
    try {
      em = localStorage.getItem(STORAGE_EMAIL) || "";
    } catch (e) {}
    if (!em && typeof window.bopfCustomerEmail === "string" && EMAIL_RE.test(window.bopfCustomerEmail)) {
      em = String(window.bopfCustomerEmail).trim().toLowerCase();
      try {
        localStorage.setItem(STORAGE_EMAIL, em);
      } catch (e2) {}
    }
    return em;
  }

  // ---- synced map (v11), now tied to the email it was built for (v12) ----
  function getSyncedOwner() {
    try {
      return localStorage.getItem(STORAGE_SYNCED_FOR) || "";
    } catch (e) {
      return "";
    }
  }

  // Pass the email in play. If the synced flags were recorded for ANOTHER email they
  // mean nothing for this one, so they are dropped: every local heart then counts as
  // "not on the server yet" and is uploaded again (safe - an upload never deletes).
  function getSyncedMap(email) {
    var m = readJSON(STORAGE_SYNCED);
    if (email) {
      var owner = lc(getSyncedOwner()), want = lc(email);
      if (owner && owner !== want) {
        m = {};
        writeJSON(STORAGE_SYNCED, m);
        log("synced flags belonged to another email - cleared");
      }
      if (owner !== want) {
        try {
          localStorage.setItem(STORAGE_SYNCED_FOR, want);
        } catch (e) {}
      }
    }
    return m;
  }

  function setSyncedMap(m) {
    writeJSON(STORAGE_SYNCED, m);
  }

  function markSynced(handle, email) {
    if (!handle) return;
    var m = getSyncedMap(email);
    m[handle] = Date.now();
    setSyncedMap(m);
  }

  function clearSynced(handle) {
    if (!handle) return;
    var m = readJSON(STORAGE_SYNCED);
    if (deleteHandle(m, handle)) setSyncedMap(m);
  }

  // ---- taps that still have to reach the server (v12) ----
  // {handle: {t, e, ...}}. An entry is retried only close to the tap (ttl): later than
  // that, a change she made in the app or another browser must win over an old, lost tap.
  function readPending(key, ttl) {
    var m = readJSON(key), out = {}, dirty = false, now = Date.now();
    Object.keys(m).forEach(function(k) {
      var v = m[k];
      if (v && typeof v === "object" && v.e && v.t && Math.abs(now - v.t) < ttl) out[k] = v; else dirty = true;
    });
    if (dirty) writeJSON(key, out);
    return out;
  }

  function clearPending(key, ttl, handle) {
    var m = readPending(key, ttl), k = lc(handle);
    if (m[k]) {
      delete m[k];
      writeJSON(key, m);
    }
  }

  // Removes (un-hearts) whose POST has not been confirmed yet.
  function getUnsavePending() {
    return readPending(STORAGE_UNSAVE_PENDING, UNSAVE_PENDING_TTL_MS);
  }

  function addUnsavePending(handle, email) {
    if (!handle || !email) return;
    var m = getUnsavePending();
    m[lc(handle)] = {
      t: Date.now(),
      e: lc(email)
    };
    writeJSON(STORAGE_UNSAVE_PENDING, m);
  }

  function clearUnsavePending(handle) {
    clearPending(STORAGE_UNSAVE_PENDING, UNSAVE_PENDING_TTL_MS, handle);
  }

  // Heart taps (email known) whose add POST has not been confirmed yet. The flush re-sends
  // these as the tap they were (source wishlist_heart), not as the background sync, so a
  // piece she removed earlier and has now hearted again is not dropped by the server.
  function getAddPending() {
    return readPending(STORAGE_ADD_PENDING, ADD_PENDING_TTL_MS);
  }

  function addAddPending(handle, email, consent) {
    if (!handle || !email) return;
    var m = getAddPending();
    m[lc(handle)] = {
      t: Date.now(),
      e: lc(email),
      c: consent ? 1 : 0
    };
    writeJSON(STORAGE_ADD_PENDING, m);
  }

  function clearAddPending(handle) {
    clearPending(STORAGE_ADD_PENDING, ADD_PENDING_TTL_MS, handle);
  }

  // When each piece was last un-hearted in this browser (kept 10 minutes). A server read
  // that started before the un-heart is older than it and must not heart the piece again.
  function getUnsavedAt() {
    var m = readJSON(STORAGE_UNSAVED_AT), out = {}, dirty = false, now = Date.now();
    Object.keys(m).forEach(function(k) {
      var t = m[k];
      if (typeof t === "number" && Math.abs(now - t) < UNSAVED_AT_TTL_MS) out[k] = t; else dirty = true;
    });
    if (dirty) writeJSON(STORAGE_UNSAVED_AT, out);
    return out;
  }

  function noteUnsaved(handle) {
    if (!handle) return;
    var m = getUnsavedAt();
    m[lc(handle)] = Date.now();
    writeJSON(STORAGE_UNSAVED_AT, m);
  }

  function clearUnsavedAt(handle) {
    var m = getUnsavedAt(), k = lc(handle);
    if (Object.prototype.hasOwnProperty.call(m, k)) {
      delete m[k];
      writeJSON(STORAGE_UNSAVED_AT, m);
    }
  }

  function postWishlistRemove(email, handle) {
    return fetch(ZARA_BACKEND + "/zara/wishlist/remove", {
      method: "POST",
      headers: {
        "Content-Type": "application/json"
      },
      body: JSON.stringify({
        email: email,
        product_handle: handle
      })
    }).then(function(r) {
      var st = r.status;
      return r.json().catch(function() {
        return {
          ok: !1
        };
      }).then(function(d) {
        d = d && typeof d === "object" ? d : {
          ok: !1
        };
        d._status = st;
        return d;
      });
    }).then(function(d) {
      // Done when the server took it, or refused it for good (4xx other than 429).
      if (d.ok || d._status >= 400 && d._status < 500 && d._status !== 429) clearUnsavePending(handle);
      return d;
    }, function(e) {
      log("remove failed, will retry on a later page:", handle, e && e.message);
      return {
        ok: !1,
        error: e && e.message
      };
    });
  }

  function retryPendingUnsaves(email) {
    var m = getUnsavePending(), want = lc(email);
    Object.keys(m).forEach(function(h, i) {
      if (m[h].e !== want) return;
      setTimeout(function() {
        if (isSaved(h)) {
          clearUnsavePending(h);
          return;
        }
        postWishlistRemove(email, h).then(function(d) {
          log("remove retry:", h, d);
        });
      }, i * 300);
    });
  }

  function flushPending(email) {
    if (!email) return;
    retryPendingUnsaves(email);
    var saved = getSavedMap(), synced = getSyncedMap(email), todo = Object.keys(saved).filter(function(h) {
      return h && !synced[h] && !synced[lc(h)];
    });
    // A tap entry is done once the piece is on the server, or no longer hearted.
    Object.keys(getAddPending()).forEach(function(k) {
      var sk = findKey(synced, k);
      if (!isSaved(k) || sk && synced[sk]) clearAddPending(k);
    });
    if (!todo.length) return;
    log("syncing local-only saves:", todo.length);
    todo.slice(0, FLUSH_MAX).forEach(function(h, i) {
      setTimeout(function() {
        // Re-check at send time: un-hearted, or confirmed by the server meanwhile?
        if (!isSaved(h)) return;
        var syn = getSyncedMap(email);
        if (syn[h] || syn[lc(h)]) return;
        fetch("/products/" + encodeURIComponent(h) + ".js").then(function(r) {
          if (r.status === 404) return !1;
          return r.ok ? r.json() : null;
        }).then(function(p) {
          if (p === !1) {
            // Sold or unpublished: not sent (as v11).
            markSynced(h, email);
            clearAddPending(h);
            log("sync: gone from the store, not sent:", h);
            return;
          }
          if (!p) {
            // Store hiccup (429/5xx): leave it unsynced and try again on a later page.
            log("sync: store lookup failed, will retry:", h);
            return;
          }
          var img = p.featured_image ? String(p.featured_image).indexOf("//") === 0 ? "https:" + p.featured_image : String(p.featured_image) : "";
          // A heart she tapped with her email known, whose own POST was lost (within the
          // hour), is re-sent as that tap: it clears an earlier removal, as the tap meant
          // to. Everything else is the background sync, which the server drops for a piece
          // she removed. silent: no confirmation email and no rate limit either way.
          var ap = getAddPending()[lc(h)], explicit = !!(ap && ap.e === lc(email)), payload = {
            email: email,
            product_handle: h,
            product_title: p.title || h,
            product_price_aed: null,
            product_image: img,
            product_url: location.origin + "/products/" + h,
            source: explicit ? "wishlist_heart" : "wishlist_heart_sync",
            silent: !0
          };
          if (explicit) payload.consent_marketing = !!ap.c;
          // When she hearted it here (ISO). Informational: lets the server tell a heart
          // made after a removal from an old one. Servers up to v3.98 ignore it.
          var sm = getSavedMap(), sk = findKey(sm, h);
          if (sk && typeof sm[sk] === "number" && sm[sk] > 0) {
            try {
              payload.saved_at = new Date(sm[sk]).toISOString();
            } catch (eT) {}
          }
          return fetch(ZARA_BACKEND + "/zara/wishlist/add", {
            method: "POST",
            headers: {
              "Content-Type": "application/json"
            },
            body: JSON.stringify(payload)
          }).then(function(r) {
            var st = r.status;
            return r.json().catch(function() {
              return {
                ok: !1
              };
            }).then(function(d) {
              d = d && typeof d === "object" ? d : {
                ok: !1
              };
              d._status = st;
              return d;
            });
          }).then(function(d) {
            // A tap refused for good (4xx other than 429) goes back to the normal sync.
            if (explicit && !d.ok && d._status >= 400 && d._status < 500 && d._status !== 429) clearAddPending(h);
            if (d && d.ok) {
              markSynced(h, email);
              clearAddPending(h);
              if (d.suppressed) {
                // She removed this piece somewhere else - clear the heart here too.
                markUnsaved(h);
                log("sync: removed elsewhere, heart cleared:", h);
              }
            }
            log("sync:", h, d);
          });
        }).catch(function(e) {
          log("sync failed:", h, e && e.message);
        });
      }, i * 400);
    });
  }

  function setEmail(email) {
    try {
      localStorage.setItem(STORAGE_EMAIL, email);
    } catch (e) {}
  }

  function getSavedMap() {
    return readJSON(STORAGE_SAVED);
  }

  function setSavedMap(m) {
    writeJSON(STORAGE_SAVED, m);
  }

  function isSaved(handle) {
    if (!handle) return !1;
    var m = getSavedMap(), k = findKey(m, handle);
    return !!(k && m[k]);
  }

  function markSaved(handle) {
    var m = getSavedMap(), k = findKey(m, handle) || handle;
    m[k] = Date.now();
    setSavedMap(m);
    // A fresh tap is not on the server until its POST says so (v12), and it
    // cancels any remove of the same piece that is still waiting to be sent.
    clearSynced(handle);
    clearUnsavePending(handle);
    clearUnsavedAt(handle);
    fireWishlistChanged();
  }

  function markUnsaved(handle) {
    var m = getSavedMap();
    deleteHandle(m, handle);
    setSavedMap(m);
    clearSynced(handle);
    // v12: an un-heart cancels a tap still waiting to be sent, and is remembered for
    // 10 minutes so a server read that started before it cannot heart the piece again.
    clearAddPending(handle);
    noteUnsaved(handle);
    fireWishlistChanged();
  }

  function fireWishlistChanged(quiet) {
    try {
      window.dispatchEvent(new CustomEvent("bopf-wishlist-changed", {
        detail: {
          count: Object.keys(getSavedMap()).length,
          quiet: !!quiet
        }
      }));
    } catch (e) {}
  }

  function recentlyDeclined() {
    try {
      var t = parseInt(localStorage.getItem(STORAGE_DECLINED) || "0", 10);
      return t && Date.now() - t < DECLINED_COOLDOWN_MS;
    } catch (e) {
      return !1;
    }
  }

  function markDeclined() {
    try {
      localStorage.setItem(STORAGE_DECLINED, String(Date.now()));
    } catch (e) {}
  }

  function getPageType() {
    var p = window.location.pathname;
    return /\/products\/[^\/]+/.test(p) ? "product" : /\/collections\//.test(p) ? "collection" : /\/search/.test(p) ? "search" : "other";
  }

  function getProductHandleFromUrl(url) {
    var m = String(url || "").match(/\/products\/([^\/?#]+)/);
    return m ? m[1] : "";
  }

  // "chanel-classic-flap-bag" -> "Chanel Classic Flap Bag"
  function humanizeHandle(h) {
    var s = String(h || "");
    try {
      s = decodeURIComponent(s);
    } catch (e) {}
    return s.replace(/[-_]+/g, " ").replace(/\s+/g, " ").trim().replace(/(^|\s)([a-z])/g, function(all, sp, c) {
      return sp + c.toUpperCase();
    });
  }

  // The live theme puts the LOGO in an <h1 class="logo" data-logo> inside the header,
  // so a bare "h1" would read the logo. Skip anything in the header, nav or logo.
  function inHeaderOrLogo(el) {
    for (var n = el; n && n.nodeType === 1; n = n.parentElement) {
      var tag = String(n.tagName || "").toLowerCase(), cls = typeof n.className === "string" ? " " + n.className + " " : "";
      if (tag === "header" || tag === "nav") return !0;
      if (cls.indexOf(" logo ") !== -1 || n.hasAttribute && n.hasAttribute("data-logo")) return !0;
    }
    return !1;
  }

  var PDP_TITLE_SELECTORS = [ "[data-product-title]", ".product__title", ".product-single__title", ".product-title", 'h1[class*="product"]', '[class*="product"] h1', "main h1", "h1" ];

  function readProductHeading() {
    for (var i = 0; i < PDP_TITLE_SELECTORS.length; i++) {
      var els;
      try {
        els = document.querySelectorAll(PDP_TITLE_SELECTORS[i]);
      } catch (e) {
        continue;
      }
      for (var j = 0; j < els.length; j++) {
        if (inHeaderOrLogo(els[j])) continue;
        var t = sanitizeText(els[j].textContent);
        if (t) return t;
      }
    }
    return "";
  }

  function readCurrentProductContext() {
    var ctx = {
      handle: "",
      title: "",
      image: "",
      price: null,
      url: ""
    }, canonicalLink = document.querySelector('link[rel="canonical"]'), canonicalHref = canonicalLink ? canonicalLink.getAttribute("href") : "";
    if (canonicalHref) {
      ctx.handle = getProductHandleFromUrl(canonicalHref);
      ctx.url = canonicalHref;
    }
    if (!ctx.handle) {
      ctx.handle = getProductHandleFromUrl(window.location.pathname);
      ctx.url = window.location.origin + window.location.pathname;
    }
    // Title: og:title -> document.title -> the product heading -> the handle (v12).
    var rawTitle = "", ogTitle = document.querySelector('meta[property="og:title"]');
    if (ogTitle) rawTitle = ogTitle.getAttribute("content") || "";
    if (!sanitizeText(rawTitle)) rawTitle = (document.title || "").split("|")[0];
    ctx.title = sanitizeText(rawTitle);
    if (!ctx.title) ctx.title = readProductHeading();
    if (!ctx.title && ctx.handle) ctx.title = humanizeHandle(ctx.handle);
    var ogImage = document.querySelector('meta[property="og:image"]');
    if (ogImage) ctx.image = (ogImage.getAttribute("content") || "").trim();
    var priceMeta = document.querySelector('meta[property="product:price:amount"]');
    if (priceMeta) ctx.price = parseFloat(priceMeta.getAttribute("content")) || null;
    return ctx;
  }

  function sanitizeText(s) {
    return s ? String(s).replace(/<[^>]*>/g, " ").replace(/&nbsp;/gi, " ").replace(/&amp;/g, "&").replace(/&lt;/g, "<").replace(/&gt;/g, ">").replace(/\s+/g, " ").trim() : "";
  }

  function readCardContext(cardEl) {
    var ctx = {
      handle: "",
      title: "",
      image: "",
      price: null,
      url: ""
    }, link = cardEl.querySelector('a[href*="/products/"]');
    link && (ctx.url = link.href, ctx.handle = getProductHandleFromUrl(link.getAttribute("href")));
    var img = cardEl.querySelector("img");
    img && (ctx.image = img.currentSrc || img.src || img.getAttribute("data-src") || "",
    ctx.image = ctx.image.replace(/_(?:pico|icon|thumb|small|compact|medium|large|grande|original|\d+x\d*|\d+x\d+)\./, "."));
    var titleEl = cardEl.querySelector('h2, h3, .product-card__title, [class*="card__title"], [class*="product-title"]');
    titleEl && (ctx.title = sanitizeText(titleEl.textContent)), !ctx.title && link && (ctx.title = sanitizeText(link.textContent));
    var priceEl = cardEl.querySelector('[class*="price"]');
    if (priceEl) {
      var priceText = (priceEl.textContent || "").replace(/[^0-9.,]/g, "").replace(/,/g, ""), p = parseFloat(priceText);
      !isNaN(p) && p > 0 && (ctx.price = p);
    }
    return ctx;
  }

  // Never rejects: resolves with the server reply, or {ok:false} on a network error.
  function postWishlistAdd(email, ctx, consentMarketing) {
    // v12: kept until the server confirms it, so a POST lost to a cold start, a closed
    // tab or a 429 is re-sent by the flush as this tap (not as the suppressible sync).
    addAddPending(ctx.handle, email, consentMarketing);
    var payload = JSON.stringify({
      email: email,
      product_handle: ctx.handle,
      product_title: ctx.title || humanizeHandle(ctx.handle),
      product_price_aed: ctx.price || null,
      product_image: ctx.image || "",
      product_url: ctx.url || "",
      source: "wishlist_heart",
      consent_marketing: !!consentMarketing
    });
    return fetch(ZARA_BACKEND + "/zara/wishlist/add", {
      method: "POST",
      headers: {
        "Content-Type": "application/json"
      },
      body: payload
    }).then(function(r) {
      return r.json().catch(function() {
        return {
          ok: !1
        };
      });
    }).then(function(d) {
      if (d && d.ok) {
        clearAddPending(ctx.handle);
        // Only mark it synced if it is still hearted (she may have un-hearted meanwhile).
        isSaved(ctx.handle) && markSynced(ctx.handle, email);
      }
      return d;
    }, function(e) {
      return {
        ok: !1,
        error: e && e.message
      };
    });
  }

  function injectStyles() {
    if (!document.getElementById("bopf-heart-styles")) {
      var s = document.createElement("style");
      s.id = "bopf-heart-styles", s.textContent = '.swym-wishlist-button-bar,.swym-wishlist-button,.swym-button.swym-add-to-wishlist-view-product,.swym-add-to-wishlist,.swym-add-to-wishlist-view-product,.swym-collection-add-to-wishlist-view-product,.swym-collection-add-to-wishlist-view-collection,.swym-button.swym-bulk-add-to-cart-button-cart,[data-swaction="addToWishlist"],.swym-add-to-wishlist-button,.swym-emt-add-to-wishlist,#swym-atw-pdp-button,.atw-button,.atw-button-icon,[id^="swym-atw-"],[class*="atw-button"],a[href="#swym-wishlist"],.swym-wishlist,.swym-launcher-header-icon,[id^="swym-inject-header"] {display: none !important; visibility: hidden !important;}.bopf-card-heart {position: absolute; top: 12px; right: 12px;width: 32px; height: 32px;background: rgba(255,255,255,0.85);border: none;border-radius: 50%;cursor: pointer;display: flex; align-items: center; justify-content: center;padding: 0;transition: background 0.15s, transform 0.1s;z-index: 5;}.bopf-card-heart:hover {background: rgba(255,255,255,1);}.bopf-card-heart:active {transform: scale(0.92);}.bopf-card-heart svg {width: 20px; height: 20px;stroke: #1F1A17;stroke-width: 1.6;fill: none;transition: fill 0.18s, stroke 0.18s;}.bopf-card-heart.is-saved svg {fill: #A0563E;stroke: #A0563E;}.bopf-card-anchor {position: relative !important;}.bopf-product-heart {display: inline-flex; align-items: center; justify-content: center;width: 38px; height: 38px;background: transparent;border: 1px solid #d6cfc4;border-radius: 50%;cursor: pointer;padding: 0;margin-left: 12px;vertical-align: middle;transition: border-color 0.18s, background 0.18s, transform 0.1s;flex-shrink: 0;}.bopf-product-heart:hover {border-color: #A0563E;background: rgba(160, 86, 62, 0.05);}.bopf-product-heart:active {transform: scale(0.92);}.bopf-product-heart svg {width: 20px; height: 20px;stroke: #1F1A17;stroke-width: 1.6;fill: none;transition: fill 0.25s ease, stroke 0.18s;}.bopf-product-heart:hover svg {stroke: #A0563E;}.bopf-product-heart.is-saved {border-color: #A0563E;background: rgba(160, 86, 62, 0.08);}.bopf-product-heart.is-saved svg {fill: #A0563E;stroke: #A0563E;}.bopf-product-heart.pulse svg, .bopf-card-heart.pulse svg {animation: bopfHeartPulse 0.28s ease;}@keyframes bopfHeartPulse {0%   { transform: scale(1); }40%  { transform: scale(1.25); }100% { transform: scale(1); }}.bopf-product-heart-label {display: inline-block;margin-left: 8px;font-size: 11px;letter-spacing: 0.08em;text-transform: uppercase;color: #888;font-style: italic;font-family: Georgia, serif;vertical-align: middle;transition: color 0.18s;}.bopf-product-heart-wrap:hover .bopf-product-heart-label {color: #A0563E;}.bopf-product-heart-wrap.is-saved .bopf-product-heart-label {color: #A0563E;}@media (max-width: 640px) {.bopf-product-heart-label { display: none; }}.bopf-product-heart-wrap {display: inline-flex; align-items: center;margin-top: 8px;}.bopf-product-button {display: flex; align-items: center; justify-content: center;gap: 10px;width: 100%;padding: 14px 24px;background: #1F1A17;color: #FAF6F0;border: 1px solid #1F1A17;font-family: inherit;font-size: 12px;letter-spacing: 0.18em;text-transform: uppercase;cursor: pointer;margin: 12px 0;transition: background 0.15s, color 0.15s;}.bopf-product-button:hover {background: #2c2521;}.bopf-product-button:active {transform: scale(0.99);}.bopf-product-button svg {width: 16px; height: 16px;stroke: #FAF6F0;stroke-width: 1.6;fill: none;transition: fill 0.18s;}.bopf-product-button.is-saved {background: #FAF6F0;color: #1F1A17;border-color: #1F1A17;}.bopf-product-button.is-saved svg {fill: #A0563E;stroke: #A0563E;}.bopf-nav-heart {display: inline-flex; align-items: center; justify-content: center;position: relative;width: 36px; height: 36px;background: transparent; border: none; padding: 0;color: inherit; text-decoration: none; cursor: pointer;vertical-align: middle;transition: transform 0.12s ease;}.bopf-nav-heart svg {width: 22px; height: 22px;stroke: currentColor; stroke-width: 1.6; fill: none;transition: fill 0.22s ease, stroke 0.22s ease, transform 0.22s ease;}.bopf-nav-heart:hover svg {stroke: #A0563E;}.bopf-nav-heart.has-saved svg {fill: #A0563E; stroke: #A0563E;}.bopf-nav-heart-badge {position: absolute; top: 2px; right: 0;min-width: 16px; height: 16px; padding: 0 4px;background: #A0563E; color: #FAF6F0;border-radius: 8px;font-size: 10px; font-weight: 600; line-height: 16px;text-align: center;font-family: -apple-system, BlinkMacSystemFont, "Helvetica Neue", Arial, sans-serif;box-sizing: border-box;pointer-events: none;opacity: 0; transform: scale(0.6);transition: opacity 0.18s ease, transform 0.18s cubic-bezier(0.34, 1.56, 0.64, 1);}.bopf-nav-heart.has-saved .bopf-nav-heart-badge {opacity: 1; transform: scale(1);}.bopf-nav-heart.pulse svg {animation: bopfNavHeartPulse 0.42s cubic-bezier(0.34, 1.56, 0.64, 1);}.bopf-nav-heart.pulse .bopf-nav-heart-badge {animation: bopfNavBadgePop 0.42s cubic-bezier(0.34, 1.56, 0.64, 1);}@keyframes bopfNavHeartPulse {0%   { transform: scale(1); }40%  { transform: scale(1.32); }100% { transform: scale(1); }}@keyframes bopfNavBadgePop {0%   { transform: scale(1); }40%  { transform: scale(1.4); }100% { transform: scale(1); }}.bopf-nav-heart--mobile {width: 44px; height: 44px;flex: 0 0 auto;}.bopf-nav-heart--mobile svg {width: 22px; height: 22px;}.bopf-nav-heart--mobile .bopf-nav-heart-badge {top: 6px; right: 4px;}.bopf-wl-overlay{position:fixed;top:0;left:0;right:0;bottom:0;z-index:99998;background:rgba(20,15,12,0.55);backdrop-filter:blur(2px);display:flex;align-items:center;justify-content:center;padding:20px;animation:bopfWlFade .18s ease}@keyframes bopfWlFade{from{opacity:0}to{opacity:1}}.bopf-wl-card{background:#FAF6F0;border-radius:8px;width:100%;max-width:420px;padding:30px 28px 26px;box-shadow:0 20px 60px rgba(20,15,12,.25);position:relative;font-family:-apple-system,BlinkMacSystemFont,"Helvetica Neue",Arial,sans-serif;color:#1F1A17}.bopf-wl-x{position:absolute;top:10px;right:12px;background:none;border:none;font-size:24px;line-height:1;color:#999;cursor:pointer;padding:6px 10px;border-radius:50%}.bopf-wl-x:hover{background:rgba(0,0,0,.05);color:#1F1A17}.bopf-wl-eyebrow{font-size:11px;letter-spacing:0.18em;text-transform:uppercase;color:#A0563E;margin:0 0 8px 0}.bopf-wl-headline{font-family:Georgia,"Times New Roman",serif;font-size:24px;line-height:1.25;color:#1F1A17;margin:0 0 12px 0;font-weight:400}.bopf-wl-headline em{font-style:italic;color:#A0563E;position:relative;display:inline-block}.bopf-wl-headline em:after{content:"";position:absolute;left:0;right:0;bottom:-4px;height:6px;background-image:url(\'data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 90 6"><path d="M2 3 Q 14 1, 24 3 T 44 3 T 64 3 T 88 3" stroke="%23A0563E" stroke-width="1.4" fill="none" stroke-linecap="round"/></svg>\');background-repeat:no-repeat;background-size:100% 100%}.bopf-wl-body{font-size:14px;line-height:1.55;color:#3a3530;margin:0 0 18px 0}.bopf-wl-input{width:100%;padding:12px 14px;font-size:15px;font-family:inherit;color:#1F1A17;border:1px solid #d6cfc4;background:#fff;border-radius:4px;box-sizing:border-box;margin-bottom:12px;transition:border-color .15s}.bopf-wl-input:focus{outline:none;border-color:#A0563E}.bopf-wl-checkbox-row{display:flex;gap:8px;align-items:flex-start;margin-bottom:18px;font-size:13px;color:#666;line-height:1.45}.bopf-wl-checkbox-row input{margin-top:3px;flex-shrink:0;cursor:pointer}.bopf-wl-checkbox-row label{cursor:pointer}.bopf-wl-actions{display:flex;gap:10px;align-items:center}.bopf-wl-btn{padding:12px 22px;font-size:13px;letter-spacing:0.05em;font-family:inherit;border-radius:4px;cursor:pointer;border:none;transition:all .12s;font-weight:500}.bopf-wl-btn-primary{background:#A0563E;color:#fff;flex:1}.bopf-wl-btn-primary:hover{background:#8a4732}.bopf-wl-btn-primary:disabled{opacity:.5;cursor:not-allowed}.bopf-wl-btn-text{background:none;color:#999;text-decoration:underline;padding:8px 4px}.bopf-wl-btn-text:hover{color:#1F1A17}.bopf-wl-error{font-size:13px;color:#a8443a;margin:8px 0 0 0;min-height:18px}.bopf-wl-fineprint{font-size:11px;color:#999;margin-top:14px;line-height:1.4}.bopf-wl-fineprint a{color:#666}@media (max-width:480px){.bopf-wl-card{padding:24px 20px 22px}.bopf-wl-headline{font-size:21px}}',
      document.head.appendChild(s);
    }
  }

  var HEART_SVG = '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 21s-7-4.5-7-10.5C5 7 7.5 5 10 5c1.5 0 2.6.7 3 1.5C13.4 5.7 14.5 5 16 5c2.5 0 5 2 5 5.5C21 16.5 12 21 12 21z" stroke-linejoin="round" stroke-linecap="round"/></svg>';

  function escapeHtml(s) {
    return String(s || "").replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");
  }

  function openModal(ctx, onSubmit, onCancel) {
    injectStyles();
    var overlay = document.createElement("div");
    overlay.className = "bopf-wl-overlay", overlay.innerHTML = '<div class="bopf-wl-card" role="dialog" aria-labelledby="bopf-wl-h"><button class="bopf-wl-x" aria-label="Close" data-bopf-wl-close>&times;</button><p class="bopf-wl-eyebrow">A note from Hadiza</p><h2 class="bopf-wl-headline" id="bopf-wl-h">Save this piece, and I\'ll <em>let you know</em> when it\'s right.</h2><p class="bopf-wl-body">Drop your email and I\'ll personally reach out if the price comes down, if it\'s about to sell, or if I find something similar that suits you.</p><form data-bopf-wl-form><input class="bopf-wl-input" type="email" name="email" placeholder="your@email.com" required autocomplete="email" /><div class="bopf-wl-checkbox-row"><input type="checkbox" id="bopf-wl-mkt" name="marketing" /><label for="bopf-wl-mkt">Yes, also send me new arrivals and BOPF news (you can unsubscribe anytime).</label></div><div class="bopf-wl-actions"><button type="submit" class="bopf-wl-btn bopf-wl-btn-primary" data-bopf-wl-submit>Save & alert me</button><button type="button" class="bopf-wl-btn bopf-wl-btn-text" data-bopf-wl-skip>Skip</button></div><p class="bopf-wl-error" data-bopf-wl-err></p></form><p class="bopf-wl-fineprint">We only use your email for the alerts you\'ve opted into. No spam. <a href="/policies/privacy-policy" target="_blank">Privacy</a>.</p></div>',
    document.body.appendChild(overlay);
    function close() {
      overlay.parentNode && overlay.parentNode.removeChild(overlay);
    }
    overlay.addEventListener("click", function(e) {
      e.target === overlay && (close(), onCancel && onCancel());
    }), overlay.querySelector("[data-bopf-wl-close]").addEventListener("click", function() {
      close(), onCancel && onCancel();
    }), overlay.querySelector("[data-bopf-wl-skip]").addEventListener("click", function() {
      close(), onCancel && onCancel();
    });
    var form = overlay.querySelector("[data-bopf-wl-form]"), emailInput = form.querySelector('input[name="email"]'), mktCheckbox = form.querySelector('input[name="marketing"]'), submitBtn = overlay.querySelector("[data-bopf-wl-submit]"), errEl = overlay.querySelector("[data-bopf-wl-err]");
    setTimeout(function() {
      try {
        emailInput.focus();
      } catch (e) {}
    }, 50), form.addEventListener("submit", function(e) {
      e.preventDefault();
      var email = (emailInput.value || "").trim().toLowerCase();
      if (!email || !EMAIL_RE.test(email)) {
        errEl.textContent = "Please enter a valid email.";
        return;
      }
      errEl.textContent = "", submitBtn.disabled = !0, submitBtn.textContent = "Saving...";
      var consentMarketing = !!mktCheckbox.checked;
      close(), onSubmit(email, consentMarketing);
    });
    function escClose(e) {
      e.key === "Escape" && (document.removeEventListener("keydown", escClose), overlay.parentNode && (close(),
      onCancel && onCancel()));
    }
    document.addEventListener("keydown", escClose);
  }

  function handleSaveClick(ctx, heartEl) {
    if (!ctx.handle) {
      log("no product handle, skipping");
      return;
    }
    if (isSaved(ctx.handle)) {
      markUnsaved(ctx.handle), heartEl.classList.remove("is-saved");
      var btnLabel = heartEl.querySelector("[data-bopf-label]");
      btnLabel && (btnLabel.textContent = "Add to wishlist");
      // v12: with a known email the remove goes to the server too (Saved page + app).
      var emailU = getEmail();
      if (emailU) {
        addUnsavePending(ctx.handle, emailU);
        postWishlistRemove(emailU, ctx.handle).then(function(d) {
          log("remove backend:", ctx.handle, d);
        });
        log("unsaved (server remove sent):", ctx.handle);
      } else log("unsaved (local only):", ctx.handle);
      return;
    }
    var email = getEmail();
    if (email) {
      log("returning customer, silent capture:", ctx.handle), postWishlistAdd(email, ctx, !1).then(function(d) {
        log("backend:", d);
      }), markSaved(ctx.handle), heartEl.classList.add("is-saved");
      var btnLabel2 = heartEl.querySelector("[data-bopf-label]");
      btnLabel2 && (btnLabel2.textContent = "Saved");
      return;
    }
    if (recentlyDeclined()) {
      log("recently declined, local-only save:", ctx.handle), markSaved(ctx.handle), heartEl.classList.add("is-saved");
      var btnLabel3 = heartEl.querySelector("[data-bopf-label]");
      btnLabel3 && (btnLabel3.textContent = "Saved");
      return;
    }
    log("opening popup for:", ctx.handle), openModal(ctx, function(email2, consentMarketing) {
      setEmail(email2), markSaved(ctx.handle), heartEl.classList.add("is-saved");
      var btnLabel4 = heartEl.querySelector("[data-bopf-label]");
      btnLabel4 && (btnLabel4.textContent = "Saved");
      function after(d) {
        log("backend:", d), setTimeout(function() {
          // v12: load her server list (app saves) first, then send what is still local-only.
          hydrate(email2);
          flushWhenReady(email2);
        }, 600);
      }
      postWishlistAdd(email2, ctx, consentMarketing).then(after, after);
    }, function() {
      markDeclined(), markSaved(ctx.handle), heartEl.classList.add("is-saved");
      var btnLabel5 = heartEl.querySelector("[data-bopf-label]");
      btnLabel5 && (btnLabel5.textContent = "Saved"), log("declined popup, local-only save:", ctx.handle);
    });
  }

  function injectProductButton() {
    if (!document.querySelector(".bopf-product-heart-wrap, .bopf-product-button")) {
      var ctx = readCurrentProductContext();
      if (!ctx.handle) {
        log("product page but no handle detected");
        return;
      }
      var priceEl = document.querySelector('.product-info .price, .product__price, [class*="product-price"]:not([class*="compare"]), [class*="product__pricing"], [data-product-price]'), priceParent = priceEl ? priceEl.closest('.price-area, .product__price, [class*="price"]') || priceEl.parentElement : null;
      if (priceEl && priceParent) {
        var wrap = document.createElement("div");
        wrap.className = "bopf-product-heart-wrap", isSaved(ctx.handle) && wrap.classList.add("is-saved");
        var heart = document.createElement("button");
        heart.type = "button", heart.className = "bopf-product-heart", heart.setAttribute("aria-label", isSaved(ctx.handle) ? "Remove from wishlist" : "Save to wishlist"),
        heart.setAttribute("data-bopf-handle", ctx.handle),
        isSaved(ctx.handle) && heart.classList.add("is-saved"), heart.innerHTML = HEART_SVG;
        var label = document.createElement("span");
        label.className = "bopf-product-heart-label", label.setAttribute("data-bopf-label", ""),
        label.textContent = isSaved(ctx.handle) ? "Saved" : "Save this piece", wrap.appendChild(heart),
        wrap.appendChild(label), heart.addEventListener("click", function(e) {
          e.preventDefault(), e.stopPropagation(), heart.classList.add("pulse"), setTimeout(function() {
            heart.classList.remove("pulse");
          }, 320), handleSaveClick(ctx, heart), setTimeout(function() {
            heart.classList.contains("is-saved") ? (wrap.classList.add("is-saved"), label.textContent = "Saved") : (wrap.classList.remove("is-saved"),
            label.textContent = "Save this piece");
          }, 0);
        });
        var anchor = priceEl.parentElement === priceParent ? priceEl : priceParent;
        anchor.insertAdjacentElement("afterend", wrap), log("injected heart icon next to price for", ctx.handle);
        return;
      }
      log("price element not found, falling back to full-width button");
      var addToCartBtn = document.querySelector('button[name="add"], button[type="submit"][class*="add"], form[action*="/cart/add"] button[type="submit"]'), injectionParent = null, injectionRef = null;
      if (addToCartBtn) {
        var formOrSection = addToCartBtn.closest("form") || addToCartBtn.parentElement;
        formOrSection && (injectionParent = formOrSection.parentElement, injectionRef = formOrSection.nextSibling);
      }
      if (!injectionParent) {
        var swymZone = document.querySelector("#swym-atw-pdp-button, .atw-button, .swym-wishlist-button-bar, .swym-add-to-wishlist-view-product");
        swymZone && swymZone.parentNode && (injectionParent = swymZone.parentNode, injectionRef = swymZone.nextSibling);
      }
      if (!injectionParent) {
        log("no injection target found on product page");
        return;
      }
      var btn = document.createElement("button");
      btn.type = "button", btn.className = "bopf-product-button", btn.setAttribute("data-bopf-handle", ctx.handle), isSaved(ctx.handle) && btn.classList.add("is-saved"),
      btn.innerHTML = HEART_SVG + "<span data-bopf-label>" + (isSaved(ctx.handle) ? "Saved" : "Add to wishlist") + "</span>",
      btn.addEventListener("click", function(e) {
        e.preventDefault(), e.stopPropagation(), btn.classList.add("pulse"), setTimeout(function() {
          btn.classList.remove("pulse");
        }, 320), handleSaveClick(ctx, btn);
      }), injectionParent.insertBefore(btn, injectionRef), log("injected fallback product button for", ctx.handle);
    }
  }

  function injectCollectionHearts(root) {
    root = root || document;
    for (var productLinks = root.querySelectorAll('a[href*="/products/"]'), processedHandles = {}, i = 0; i < productLinks.length; i++) {
      var link = productLinks[i], handle = getProductHandleFromUrl(link.getAttribute("href"));
      if (handle) {
        var card = link.closest('li, article, [class*="product-card"], [class*="card"], [class*="grid__item"], [class*="grid-item"]');
        if (card && !card.querySelector(".bopf-card-heart")) {
          var cardKey = handle + "|" + (card._bopfId || (card._bopfId = Math.random()));
          if (!processedHandles[cardKey]) {
            processedHandles[cardKey] = !0;
            var hasImg = !!card.querySelector("img");
            if (hasImg) {
              var ctx = readCardContext(card);
              if (ctx.handle = handle, !(!ctx.handle || !ctx.title)) {
                card.classList.add("bopf-card-anchor");
                var heart = document.createElement("button");
                heart.type = "button", heart.className = "bopf-card-heart", heart.setAttribute("aria-label", isSaved(handle) ? "Remove from wishlist" : "Add to wishlist"),
                heart.setAttribute("data-bopf-handle", handle),
                isSaved(handle) && heart.classList.add("is-saved"), heart.innerHTML = HEART_SVG,
                heart.addEventListener("click", function(c, h) {
                  return function(e) {
                    e.preventDefault(), e.stopPropagation(), h.classList.add("pulse"), setTimeout(function() {
                      h.classList.remove("pulse");
                    }, 320), handleSaveClick(c, h);
                  };
                }(ctx, heart)), card.appendChild(heart);
              }
            }
          }
        }
      }
    }
  }

  // v12: bring every heart on the page in line with bopf_zara_saved (after a server
  // read, a change made by the Saved page, or a change in another tab).
  function repaintHearts() {
    var m = getSavedMap();
    function on(h) {
      var k = findKey(m, h);
      return !!(k && m[k]);
    }
    var els = document.querySelectorAll(".bopf-card-heart[data-bopf-handle], .bopf-product-heart[data-bopf-handle], .bopf-product-button[data-bopf-handle]");
    for (var i = 0; i < els.length; i++) {
      var el = els[i], saved = on(el.getAttribute("data-bopf-handle"));
      saved ? el.classList.add("is-saved") : el.classList.remove("is-saved");
      if (el.classList.contains("bopf-card-heart")) {
        el.setAttribute("aria-label", saved ? "Remove from wishlist" : "Add to wishlist");
      } else if (el.classList.contains("bopf-product-heart")) {
        el.setAttribute("aria-label", saved ? "Remove from wishlist" : "Save to wishlist");
        var wrap = el.closest(".bopf-product-heart-wrap");
        if (wrap) {
          saved ? wrap.classList.add("is-saved") : wrap.classList.remove("is-saved");
          var lab = wrap.querySelector("[data-bopf-label]"), txt = saved ? "Saved" : "Save this piece";
          lab && lab.textContent !== txt && (lab.textContent = txt);
        }
      } else {
        var lab2 = el.querySelector("[data-bopf-label]"), txt2 = saved ? "Saved" : "Add to wishlist";
        lab2 && lab2.textContent !== txt2 && (lab2.textContent = txt2);
      }
    }
  }

  // ---- v12: server read (hydration) ----
  var hydrating = null;

  function isSavedPage() {
    // /pages/saved loads the full list itself (bopf-saved.js) and merges it.
    return !!document.getElementById("bopf-saved-app");
  }

  // The 5-minute stamp is written only after a USABLE reply, so a read that fails or is
  // cut off by leaving the page is tried again on the next page.
  function hydrateAllowed(email) {
    try {
      var raw = sessionStorage.getItem(SESSION_HYDRATED), s = raw ? JSON.parse(raw) : null;
      if (s && s.e === lc(email) && Date.now() - s.t >= 0 && Date.now() - s.t < HYDRATE_EVERY_MS) return !1;
    } catch (e) {}
    return !0;
  }

  // One read at a time per browser: a short lock in localStorage (shared by every tab)
  // stops a burst of reads when several tabs open at once or pages change quickly. It
  // expires by itself (HYDRATE_LOCK_MS) if the page holding it goes away.
  function readLock() {
    try {
      var raw = localStorage.getItem(STORAGE_HYDRATE_LOCK), s = raw ? JSON.parse(raw) : null;
      return s && typeof s === "object" ? s : null;
    } catch (e) {
      return null;
    }
  }

  function lockHeld(email) {
    var s = readLock(), age = s ? Date.now() - s.t : -1;
    return !!(s && s.e === lc(email) && age >= 0 && age < HYDRATE_LOCK_MS);
  }

  function takeLock(email) {
    var t = Date.now();
    try {
      localStorage.setItem(STORAGE_HYDRATE_LOCK, JSON.stringify({
        e: lc(email),
        t: t
      }));
    } catch (e) {}
    return t;
  }

  function releaseLock(t) {
    var s = readLock();
    if (s && s.t === t) {
      try {
        localStorage.removeItem(STORAGE_HYDRATE_LOCK);
      } catch (e) {}
    }
  }

  // Resolves once no read holds the lock (checked every 250 ms, for 10 s at most).
  function lockReleased(email) {
    return new Promise(function(resolve) {
      var n = 0;
      (function check() {
        if (!lockHeld(email) || ++n > 40) {
          resolve();
          return;
        }
        setTimeout(check, 250);
      })();
    });
  }

  function stampHydrated(email) {
    try {
      sessionStorage.setItem(SESSION_HYDRATED, JSON.stringify({
        e: lc(email),
        t: Date.now()
      }));
    } catch (e) {}
  }

  // Resolves {handles, degraded, via}, {unsupported:true} (no /handles: server older
  // than v3.98), or null (no usable reply). A reply that does not say degraded:false
  // counts as degraded. No fallback to /list: that route looks up every saved piece in
  // the store, far too heavy for every page, and the Saved page reads it anyway.
  function fetchServerHandles(email) {
    var q = "?email=" + encodeURIComponent(email);
    return fetch(ZARA_BACKEND + "/zara/wishlist/handles" + q).then(function(r) {
      if (r.status === 404) return {
        unsupported: !0
      };
      return r.json().then(function(d) {
        if (!d || !d.ok || !Array.isArray(d.handles)) return null;
        return {
          handles: d.handles,
          degraded: d.degraded !== !1,
          via: "handles"
        };
      });
    });
  }

  // The merge rules (the Saved page applies the same ones):
  //  - a handle on the server  -> hearted here and marked synced
  //    (unless a remove of it is still waiting to reach the server, or it was
  //    un-hearted here after this read started)
  //  - a local heart marked synced but absent on the server -> cleared, ONLY when
  //    degraded is false and it was not saved/confirmed after this read started
  //  - a local heart never synced -> kept; the flush sends it
  function applyServerHandles(handles, degraded, startedAt, email) {
    var saved = getSavedMap(), synced = getSyncedMap(email), pending = getUnsavePending(), unsavedAt = getUnsavedAt(), now = Date.now(), want = lc(email), onServer = {}, added = 0, removed = 0, count = 0;
    (handles || []).forEach(function(h) {
      var hl = lc(h);
      if (!hl || onServer[hl]) return;
      onServer[hl] = 1;
      count++;
      if (pending[hl] && pending[hl].e === want) return;
      // Un-hearted here after this read started: the read is older than her tap.
      if (unsavedAt[hl] && unsavedAt[hl] >= startedAt) return;
      var k = findKey(saved, hl);
      if (!k) {
        k = hl;
        saved[k] = now;
        added++;
      } else if (!saved[k]) saved[k] = now;
      synced[k] = synced[k] || now;
    });
    if (!degraded) {
      Object.keys(saved).forEach(function(k) {
        if (onServer[lc(k)]) return;
        var st = synced[k] || synced[lc(k)];
        if (!st || st >= startedAt) return;
        if (saved[k] && saved[k] >= startedAt) return;
        delete saved[k];
        removed++;
      });
    }
    Object.keys(synced).forEach(function(k) {
      if (!findKey(saved, k)) delete synced[k];
    });
    setSavedMap(saved);
    setSyncedMap(synced);
    log("hydrated:", {
      server: count,
      added: added,
      removed: removed,
      degraded: !!degraded
    });
    if (added || removed) fireWishlistChanged(!0);
    repaintHearts();
    return {
      added: added,
      removed: removed
    };
  }

  function hydrate(email) {
    if (!email || !EMAIL_RE.test(email)) return null;
    if (hydrating) return hydrating;
    if (!hydrateAllowed(email)) return null;
    if (lockHeld(email)) {
      log("hydrate: another server read is in flight - skipped");
      return null;
    }
    var startedAt = Date.now(), lockT = takeLock(email), usable = !1;
    var p = fetchServerHandles(email).then(function(res) {
      if (!res) {
        log("hydrate: no usable server list");
        return;
      }
      usable = !0;
      releaseLock(lockT);
      stampHydrated(email);
      if (res.unsupported) {
        log("hydrate: server has no /zara/wishlist/handles yet (older than v3.98) - skipped");
        return;
      }
      if (lc(getEmail()) !== lc(email)) {
        log("hydrate: email changed meanwhile, ignored");
        return;
      }
      applyServerHandles(res.handles, res.degraded, startedAt, email);
    }).catch(function(e) {
      log("hydrate failed:", e && e.message);
    }).then(function() {
      // A failed read holds the lock a little longer (back-off), then any page retries.
      if (!usable) takeLock(email);
      if (hydrating === p) hydrating = null;
    });
    hydrating = p;
    return p;
  }

  function startHydration() {
    try {
      if (isSavedPage()) {
        log("saved page - the list is merged there");
        return;
      }
      var em = getEmail();
      em && hydrate(em);
    } catch (e) {}
  }

  // v11 flush, but after the server read (so pieces the server already holds are
  // marked synced first and not sent again) - this page's read, the Saved-page merge, or
  // another tab's read that holds the lock. Waits at most FLUSH_WAIT_MAX_MS.
  function flushWhenReady(email) {
    if (!email) return;
    var merging = window.bopfWishlistMerging, wait = hydrating || (merging && typeof merging.then === "function" ? merging : null) || (lockHeld(email) ? lockReleased(email) : null), done = !1;
    function go() {
      if (done) return;
      done = !0;
      try {
        var em = getEmail();
        if (em && lc(em) === lc(email)) flushPending(em);
      } catch (e) {
        log("flush error:", e && e.message);
      }
    }
    if (wait) {
      wait.then(go, go);
      setTimeout(go, FLUSH_WAIT_MAX_MS);
    } else go();
  }

  var syncListenersInstalled = !1;
  function installSyncListeners() {
    if (syncListenersInstalled) return;
    syncListenersInstalled = !0;
    window.addEventListener("bopf-wishlist-changed", function() {
      repaintHearts();
    });
    window.addEventListener("storage", function(e) {
      e.key === STORAGE_SAVED && repaintHearts();
    });
    document.addEventListener("visibilitychange", function() {
      if (document.visibilityState === "visible") startHydration();
    });
  }

  var observerInstalled = !1;
  function installObserver() {
    if (!(observerInstalled || !window.MutationObserver)) {
      observerInstalled = !0;
      var pending = !1, observer = new MutationObserver(function() {
        pending || (pending = !0, requestAnimationFrame(function() {
          pending = !1, injectCollectionHearts();
        }));
      });
      observer.observe(document.body, {
        childList: !0,
        subtree: !0
      });
    }
  }

  function init() {
    log("init, page type:", getPageType()), injectStyles();
    var pageType = getPageType();
    if (injectNavHeart(), pageType === "product") {
      injectProductButton();
      var retries = 0, retryTimer = setInterval(function() {
        if (retries++, document.querySelector(".bopf-product-button") || retries > 20) {
          clearInterval(retryTimer);
          return;
        }
        injectProductButton();
      }, 200);
    }
    pageType !== "product" && (injectCollectionHearts(), installObserver()), pageType === "product" && setTimeout(function() {
      injectCollectionHearts(), installObserver();
    }, 500);
    installSyncListeners();
    startHydration();
  }

  function getSavedCount() {
    return Object.keys(getSavedMap()).length;
  }

  function findNavInjectionPoint() {
    var mobileNav = document.querySelector(".mobile-menu");
    if (mobileNav && isVisible(mobileNav)) {
      var mobileCart = mobileNav.querySelector('a[href="/cart"], a[href*="/cart"]');
      return mobileCart ? {
        mode: "before-mobile",
        parent: mobileNav,
        before: mobileCart
      } : {
        mode: "append-mobile",
        target: mobileNav
      };
    }
    var navRoot = document.getElementById("NavStandard") || document.querySelector("nav.nav") || document.querySelector("header nav");
    if (navRoot) {
      for (var iconWrappers = navRoot.querySelectorAll(".menu__item--icon"), visibleIconWrappers = [], i = 0; i < iconWrappers.length; i++) isVisible(iconWrappers[i]) && visibleIconWrappers.push(iconWrappers[i]);
      for (var cartWrapper = null, j = 0; j < visibleIconWrappers.length; j++) if (visibleIconWrappers[j].querySelector('a[href="/cart"], a[href*="/cart"]')) {
        cartWrapper = visibleIconWrappers[j];
        break;
      }
      if (!cartWrapper && visibleIconWrappers.length > 0 && (cartWrapper = visibleIconWrappers[visibleIconWrappers.length - 1]),
      cartWrapper && cartWrapper.parentElement) return {
        mode: "before",
        parent: cartWrapper.parentElement,
        before: cartWrapper
      };
    }
    var swymSlot = document.getElementById("swym-inject-header-parent") || document.getElementById("swym-inject-header-mobile") || document.getElementById("swym-inject-header-desktop");
    if (swymSlot) return swymSlot.setAttribute("style", "display: inline-flex !important; visibility: visible !important;"),
    {
      mode: "fill",
      target: swymSlot
    };
    var iconsRow = document.querySelector('.menu__item--icons, .header__icons, [class*="header-actions"]');
    return iconsRow ? {
      mode: "append",
      target: iconsRow
    } : null;
  }

  function isVisible(el) {
    if (!el) return !1;
    var rect = el.getBoundingClientRect();
    return rect.width > 0 && rect.height > 0;
  }

  function injectNavHeart() {
    if (!document.querySelector(".bopf-nav-heart")) {
      var spot = findNavInjectionPoint();
      if (!spot) {
        log("nav injection point not found, retrying shortly");
        var retries = 0, t = setInterval(function() {
          if (retries++, document.querySelector(".bopf-nav-heart") || retries > 20) {
            clearInterval(t);
            return;
          }
          var newSpot = findNavInjectionPoint();
          newSpot && (clearInterval(t), mountNavHeart(newSpot));
        }, 200);
        return;
      }
      mountNavHeart(spot);
    }
  }

  function mountNavHeart(spot) {
    var count = getSavedCount(), a = document.createElement("a"), isMobile = spot.mode === "before-mobile" || spot.mode === "append-mobile";
    if (a.className = "bopf-nav-heart" + (isMobile ? " bopf-nav-heart--mobile mobile-menu__button" : "") + (count > 0 ? " has-saved" : ""),
    a.href = "/pages/saved", a.setAttribute("aria-label", count > 0 ? "Saved pieces (" + count + ")" : "Saved pieces"),
    a.innerHTML = HEART_SVG + '<span class="bopf-nav-heart-badge" data-bopf-count>' + (count > 99 ? "99+" : count) + "</span>",
    spot.mode === "fill") spot.target.innerHTML = "", spot.target.appendChild(a); else if (spot.mode === "before") {
      var wrapper = document.createElement("div");
      wrapper.className = "menu__item menu__item--icon", wrapper.appendChild(a), spot.parent.insertBefore(wrapper, spot.before);
    } else if (spot.mode === "append") {
      var wrapper2 = document.createElement("div");
      wrapper2.className = "menu__item menu__item--icon", wrapper2.appendChild(a), spot.target.appendChild(wrapper2);
    } else spot.mode === "before-mobile" ? spot.parent.insertBefore(a, spot.before) : spot.mode === "append-mobile" && spot.target.appendChild(a);
    log("nav heart injected, count=" + count + ", mode=" + spot.mode), window.addEventListener("bopf-wishlist-changed", function(e) {
      // A server read or a Saved-page merge updates the count without the pulse.
      updateNavHeart(!(e && e.detail && e.detail.quiet));
    }), window.addEventListener("storage", function(e) {
      e.key === "bopf_zara_saved" && updateNavHeart(!0);
    });
  }

  function updateNavHeart(animate) {
    var heart = document.querySelector(".bopf-nav-heart");
    if (heart) {
      var count = getSavedCount(), prevCount = parseInt(heart.getAttribute("data-prev-count") || "0", 10), badge = heart.querySelector("[data-bopf-count]");
      badge && (badge.textContent = count > 99 ? "99+" : count), count > 0 ? heart.classList.add("has-saved") : heart.classList.remove("has-saved"),
      heart.setAttribute("aria-label", count > 0 ? "Saved pieces (" + count + ")" : "Saved pieces"),
      animate && count > prevCount && (heart.classList.remove("pulse"), heart.offsetWidth,
      heart.classList.add("pulse"), setTimeout(function() {
        heart.classList.remove("pulse");
      }, 450)), heart.setAttribute("data-prev-count", String(count));
    }
  }

  // Small hook for bopf-saved.js v4 (upload local-only saves after the recall form).
  try {
    window.bopfWishlistHeart = {
      version: VERSION,
      flush: function(email) {
        flushWhenReady(email || getEmail());
      },
      repaint: repaintHearts
    };
  } catch (e) {}

  log(VERSION + " script parsed, readyState =", document.readyState), document.readyState === "loading" ? document.addEventListener("DOMContentLoaded", init) : init(),
  setTimeout(function() {
    try {
      flushWhenReady(getEmail());
    } catch (e) {}
  }, 2500);
})();
// END OF FILE - bopf-wishlist-heart v13
